import React, { useState, useEffect, useMemo } from "react";
import { ArrowDownUp, AlertCircle, CheckCircle } from "lucide-react";
import { TriggerInput } from "./TriggerInput";
import { useBuilder } from "@/contexts/BuilderContext";

interface TriggerVariableMappingSectionProps {
  questions: Array<{ id?: string; text: string; shortname?: string }>;
  getVariableName: (questionId: string) => string;
  onVariableNameChange: (questionId: string, value: string) => void;
}

// Validate POST variable names
const isValidVariableName = (name: string): boolean => {
  if (!name) return true; // Empty names are valid (will be omitted)

  // Variable names should follow snake_case pattern
  // Allow letters, numbers, and underscores
  // Must start with a letter
  // No spaces allowed
  const validPattern = /^[a-z][a-z0-9_]*$/;
  return validPattern.test(name);
};

interface ValidationState {
  [questionId: string]: boolean;
}

export const TriggerVariableMappingSection: React.FC<TriggerVariableMappingSectionProps> = ({
  questions,
  getVariableName,
  onVariableNameChange,
}) => {
  const [validationState, setValidationState] = useState<ValidationState>({});

  // Get Data Collection fields from unified context
  const { getSettings } = useBuilder();
  const settings = getSettings();
  const aiConfig = settings?.ai_chat_config;

  // Combine questions and data collection fields for variable mapping
  const mappingOptions = useMemo(() => {
    const options: Array<{ id: string; label: string; type: 'question' | 'data_field' }> = [];

    // Add regular questions
    questions.forEach(question => {
      if (question.id) {
        options.push({
          id: question.id,
          label: question.text || question.shortname || 'Untitled Question',
          type: 'question'
        });
      }
    });

    // Add Data Collection fields if AI-only mode is enabled
    if (aiConfig?.collectUserInfo) {
      // Add required fields
      aiConfig.requiredFields?.forEach(field => {
        const fieldName = typeof field === 'string' ? field : field.name;
        const fieldDesc = typeof field === 'string' ? '' : field.description;
        options.push({
          id: `data_field:${fieldName}`,
          label: `${fieldName}${fieldDesc ? ` (${fieldDesc})` : ''} [Required]`,
          type: 'data_field'
        });
      });

      // Add optional fields
      aiConfig.optionalFields?.forEach(field => {
        const fieldName = typeof field === 'string' ? field : field.name;
        const fieldDesc = typeof field === 'string' ? '' : field.description;
        options.push({
          id: `data_field:${fieldName}`,
          label: `${fieldName}${fieldDesc ? ` (${fieldDesc})` : ''} [Optional]`,
          type: 'data_field'
        });
      });
    }

    return options;
  }, [questions, aiConfig]);

  // Validate all variable names when options or mappings change
  useEffect(() => {
    const newValidationState: ValidationState = {};

    mappingOptions.forEach(option => {
      const value = getVariableName(option.id);
      newValidationState[option.id] = isValidVariableName(value);
    });

    setValidationState(newValidationState);
  }, [mappingOptions, getVariableName]);

  // Handle variable name change with validation
  const handleVariableNameChange = (questionId: string, value: string) => {
    // Update validation state
    setValidationState(prev => ({
      ...prev,
      [questionId]: isValidVariableName(value)
    }));

    // Pass to parent component
    onVariableNameChange(questionId, value);
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-medium text-gray-700 flex items-center">
          <ArrowDownUp className="h-4 w-4 mr-1" />
          POST Variable Mappings
        </h4>
      </div>

      <p className="text-xs text-gray-500">
        Map questions to POST variables that will be sent to your webhook URL. Use snake_case format (e.g., first_name, email_address).
      </p>

      <div className="space-y-2">
        {mappingOptions.length > 0 ? (
          <div className="grid grid-cols-3 gap-4">
            <div className="text-sm font-medium text-gray-700 p-2">Question</div>
            <div className="text-sm font-medium text-gray-700 p-2">Key Name</div>
            <div className="text-sm font-medium text-gray-700 p-2">POST Variable Name</div>

            {mappingOptions.map((option) => (
              <React.Fragment key={option.id}>
                <div className="p-2 bg-gray-50 rounded text-sm">
                  {option.label}
                </div>
                <div>
                  <input
                    type="text"
                    value={option.type === 'question' ? (questions.find(q => q.id === option.id)?.shortname || '') : option.id.replace('data_field:', '')}
                    disabled
                    className="w-full text-sm p-2 border rounded bg-gray-50 text-gray-600"
                    placeholder={option.type === 'question' ? "No shortname" : "Field name"}
                  />
                </div>
                <div>
                  <div className="relative">
                    <TriggerInput
                      initialValue={getVariableName(option.id)}
                      onSave={(value) => handleVariableNameChange(option.id, value)}
                      className={`w-full text-sm p-2 pr-9 border rounded ${
                        getVariableName(option.id) && !validationState[option.id]
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : getVariableName(option.id)
                            ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                            : ''
                      }`}
                      placeholder={option.type === 'question'
                        ? (questions.find(q => q.id === option.id)?.shortname || "user_response")
                        : option.id.replace('data_field:', '')
                      }
                      validationFn={isValidVariableName}
                      showValidation={true}
                    />
                    {getVariableName(option.id) && (
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        {validationState[option.id] ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    )}
                  </div>
                  {getVariableName(option.id) && !validationState[option.id] && (
                    <p className="text-xs text-red-500 mt-1">
                      Use snake_case (e.g., user_response). Start with a letter, use only lowercase letters, numbers, and underscores.
                    </p>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500 italic">
            No fields available for mapping. Add questions in the Flow tab or enable Data Collection in AI settings.
          </div>
        )}
      </div>
    </div>
  );
};
